import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { IconWrench, IconBuilding, IconShield, IconBolt, IconChip, IconServer } from '../components/admin/icons';
import type { ComponentType, SVGProps } from 'react';
import { useI18n } from '../i18n';

/**
 * Page hub admin ZCE — accessible uniquement au super_admin.
 * Sidebar gauche avec navigation + contenu à droite via <Outlet />.
 */
export default function AdminPage() {
  const { user, logout } = useAuth();
  const { t } = useI18n();

  // Sur une instance client dédiée, pas de feature "Déploiements".
  const isClientInstance = import.meta.env.VITE_IS_CLIENT_INSTANCE === 'true';
  const links: Array<{ to: string; label: string; Icon: ComponentType<SVGProps<SVGSVGElement>>; desc: string }> = [
    { to: '/admin/installers',   label: t('adm_nav_installers'),   Icon: IconWrench,   desc: t('adm_nav_installers_desc') },
    { to: '/admin/clients',      label: t('adm_nav_clients'),      Icon: IconBuilding, desc: t('adm_nav_clients_desc') },
    { to: '/admin/support-logs', label: t('adm_nav_support_logs'), Icon: IconShield,   desc: t('adm_nav_support_logs_desc') },
    ...(isClientInstance
      ? [{ to: '/admin/my-infra', label: 'Mon infrastructure', Icon: IconServer, desc: 'Serveur dédié Scaleway' }]
      : [{ to: '/admin/deployments', label: 'Déploiements', Icon: IconServer, desc: 'Instances client Scaleway' }]),
    { to: '/admin/ota',          label: 'OTA',                     Icon: IconBolt,     desc: t('adm_nav_ota_desc') },
    { to: '/admin/device-models', label: t('adm_nav_device_models'), Icon: IconChip,  desc: t('adm_nav_device_models_desc') },
  ];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header — gradient cohérent avec hero "Mes Installateurs" */}
      <header className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-teal-900 shadow-xl flex-shrink-0">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(20,184,166,0.6) 0%, transparent 50%), radial-gradient(circle at 85% 50%, rgba(6,182,212,0.4) 0%, transparent 50%)'
        }} />
        <div className="relative px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <span className="text-white font-bold">Z</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white tracking-tight">
                  {t('pg_admin_header_dashboard')} <span className="bg-gradient-to-r from-teal-300 to-cyan-300 bg-clip-text text-transparent">ZCE</span>
                </h1>
                <p className="text-xs text-slate-300">{t('adm_console_super_admin')}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white font-medium">{user?.firstName || user?.email?.split('@')[0]}</p>
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-[10px] font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  {t('pg_role_super_admin')}
                </div>
              </div>
              <button
                onClick={() => { logout(); window.location.href = '/login'; }}
                className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title={t('adm_logout')}
                aria-label={t('adm_logout')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-64 bg-white border-r border-slate-200 flex-shrink-0 flex flex-col">
          <nav className="p-4 space-y-1.5">
            {links.map(link => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) =>
                  `group flex items-center gap-3 px-3.5 py-3 rounded-xl text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-md shadow-cyan-500/30'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                      isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600 group-hover:bg-white'
                    }`}>
                      <link.Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0">
                      <p className={isActive ? 'font-semibold' : ''}>{link.label}</p>
                      <p className={`text-[11px] font-normal ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{link.desc}</p>
                    </div>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Carte plateforme */}
          <div className="mt-auto mx-4 mb-4 relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-900 to-teal-900 p-4 text-white shadow-lg">
            <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-teal-400/20 blur-xl" />
            <div className="relative">
              <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-[10px] font-semibold mb-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                {t('adm_platform')}
              </div>
              <p className="text-2xl font-bold tracking-tight">ZCE</p>
              <p className="text-[11px] text-slate-300 mt-0.5">Zero Carbon Emission</p>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
